import { useEffect, useState, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Box,
    Chip,
    Button,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';

import { db, auth } from '../firebase';
import {
    collection,
    query,
    orderBy,
    getDocs,
} from 'firebase/firestore';
import { UserContext } from '../contexts/UserContext';

// ====== THEME TOKENS (match Arena) ======
const GOLD = '#d4af37';
const TEXT_WARM = '#f9f6e3';

const panelBorder = (opacity = 0.4) => `1px solid ${alpha(GOLD, opacity)}`;

const chipOutlineSx = {
    px: 1.25,
    color: TEXT_WARM,
    borderColor: alpha(GOLD, 0.55),
    background: alpha('#000', 0.3),
    fontWeight: 600,
};

const fmt = (n) => Number(n ?? 0).toLocaleString();

function ArenaLeaderBoard({ open, onClose }) {
    const { userData } = useContext(UserContext) || {};
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [myRank, setMyRank] = useState(null);
    const [totalPlayers, setTotalPlayers] = useState(0);

    const currentUid = auth?.currentUser?.uid || userData?.uid || null;

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        const loadLeaderboard = async () => {
            setLoading(true);
            setError(null);
            setRows([]);
            setMyRank(null);
            setTotalPlayers(0);

            try {
                const usersRef = collection(db, 'publicUsers');
                // NOTE: This fetches the whole collection once, ordered by trophies desc.
                // Fine for a few thousand players; can be moved server-side later if needed.
                const qLB = query(usersRef, orderBy('trophies', 'desc'));
                const snap = await getDocs(qLB);

                if (cancelled) return;

                const list = [];
                let rank = 1;
                let foundRank = null;

                snap.forEach((docSnap) => {
                    const data = docSnap.data() || {};
                    if (rank <= 1000) {
                        list.push({
                            id: docSnap.id,
                            rank,
                            username: data.username || 'Unknown',
                            guildName: data.guildName || 'No Guild',
                            league: data.league || 'Unranked',
                            trophies: Number(data.trophies || 0),
                        });
                    }

                    if (docSnap.id === currentUid && foundRank == null) {
                        foundRank = rank;
                    }

                    rank += 1;
                });

                setRows(list);
                setMyRank(foundRank);
                setTotalPlayers(rank - 1);
            } catch (e) {
                console.error('[ArenaLeaderBoard] load error:', e);
                if (!cancelled) setError(e.message || String(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadLeaderboard();

        return () => {
            cancelled = true;
        };
    }, [open, currentUid]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
                    borderRadius: 2,
                    border: panelBorder(0.4),
                    boxShadow: `0 0 0 1px ${alpha(GOLD, 0.2)} inset, 0 18px 40px ${alpha(GOLD, 0.25)}`,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: TEXT_WARM,
                    fontFamily: `'Cinzel', serif`,
                    letterSpacing: '.8px',
                    borderBottom: panelBorder(0.3),
                }}
            >
                Arena Global Leaderboard
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon sx={{ color: GOLD }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                    spacing={1.5}
                >

                    {totalPlayers > 0 && (
                        <Chip
                            size="small"
                            sx={chipOutlineSx}
                            label={
                                myRank
                                    ? `Your Rank: #${fmt(myRank)} / ${fmt(totalPlayers)}`
                                    : `Your Rank: Unranked / ${fmt(totalPlayers)}`
                            }
                        />
                    )}
                </Stack>

                {error && (
                    <Typography
                        variant="caption"
                        sx={{ color: alpha('#ff6b6b', 0.9), mb: 1, display: 'block' }}
                    >
                        Failed to load leaderboard: {error}
                    </Typography>
                )}

                <Box
                    sx={{
                        borderRadius: 2,
                        border: panelBorder(0.4),
                        overflow: 'hidden',
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow
                                sx={{
                                    background: alpha(GOLD, 0.15),
                                }}
                            >
                                <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>Rank</TableCell>
                                <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>Player</TableCell>
                                <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>Guild</TableCell>
                                <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>League</TableCell>
                                <TableCell sx={{ color: TEXT_WARM, fontFamily: `'Cinzel', serif` }}>Trophies</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ color: TEXT_WARM }}>
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ color: TEXT_WARM }}>
                                        No players found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => {
                                    const isSelf = row.id === currentUid;
                                    return (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                background: isSelf
                                                    ? alpha(GOLD, 0.18)
                                                    : 'transparent',
                                                '&:nth-of-type(even)': {
                                                    background: isSelf
                                                        ? alpha(GOLD, 0.22)
                                                        : alpha('#000', 0.4),
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ color: TEXT_WARM }}>
                                                #{fmt(row.rank)}
                                            </TableCell>
                                            <TableCell sx={{ color: TEXT_WARM }}>
                                                {row.username}
                                            </TableCell>
                                            <TableCell sx={{ color: TEXT_WARM }}>
                                                {row.guildName}
                                            </TableCell>
                                            <TableCell sx={{ color: TEXT_WARM }}>
                                                {row.league}
                                            </TableCell>
                                            <TableCell sx={{ color: TEXT_WARM }}>
                                                {fmt(row.trophies)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: panelBorder(0.3),
                    px: 2,
                    py: 1.5,
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        color: TEXT_WARM,
                        textTransform: 'none',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ArenaLeaderBoard;
